import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { requestYieldCallback } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.#deferCartOpenAfterPaint(() => {
        if (!this.isConnected) return;
        this.showDialog();
      });
    }
  };

  /**
   * Run open work after the pointer/click handler returns so INP processing
   * duration stays low (same pattern as header-drawer toggle).
   * @param {() => void} task
   */
  #deferCartOpenAfterPaint(task) {
    if (typeof scheduler !== 'undefined' && typeof scheduler.postTask === 'function') {
      scheduler.postTask(task, { priority: 'user-visible' });
      return;
    }
    requestYieldCallback(task);
  }

  open() {
    const isMobile =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(max-width: 749px)').matches;

    const dialogEl = this.querySelector('.cart-drawer__dialog');
    const isFirstMobileOpen = isMobile && !this._hasOpenedOnce;

    const runOpen = () => {
      if (isFirstMobileOpen && dialogEl) {
        this._hasOpenedOnce = true;
        dialogEl.setAttribute('data-no-animation', 'true');
      }

      this.showDialog();

      if (isFirstMobileOpen && dialogEl) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTimeout(() => {
              dialogEl.removeAttribute('data-no-animation');
            }, 50);
          });
        });
      }

      /**
       * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
       */
      customElements.whenDefined('shopify-payment-terms').then(() => {
        const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
        const cta = installmentsContent?.querySelector('#shopify-installments-cta');
        cta?.addEventListener('click', this.closeDialog, { once: true });
      });
    };

    this.#deferCartOpenAfterPaint(runOpen);
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}


// Drawer Swiper safe init script
(function () {
  // Swiper options used for init
  const SWIPER_OPTIONS = {
    spaceBetween: 16,
    mousewheel: { releaseOnEdges: true },
    pagination: { el: ".swiper-pagination", clickable: true },
    breakpoints: {
      0: { slidesPerView: 1.15 },
      749: { slidesPerView: 1.26 },
    },
  };

  // Initialize or re-init all Swipers in drawer
  function initAllDrawerSwipers() {
    try {
      const nodes = document.querySelectorAll(".mySwiper-bane_offer-drawer");
      if (!nodes.length) return;

      nodes.forEach((el) => {
        // If a Swiper instance already exists, destroy it cleanly
        if (el.swiper) {
          try {
            el.swiper.destroy(true, true);
          } catch (err) {
            // swallow individual destroy errors and continue
            console.warn("swiper destroy failed:", err);
          }
        }

        // Create fresh Swiper instance
        // eslint-disable-next-line no-undef
        new Swiper(el, SWIPER_OPTIONS);
      });
    } catch (err) {
      console.error("initAllDrawerSwipers error:", err);
    }
  }

  // Helper: schedule init shortly after DOM updates finish
  function scheduleInit(delay = 50) {
    setTimeout(initAllDrawerSwipers, delay);
  }

  // Attach listeners for theme/section update events (works for most Shopify themes)
  function attachEventListeners() {
    // Common custom event used by many themes when cart/sections update
    document.addEventListener("cart:updated", () => scheduleInit());
    document.addEventListener("shopify:section:load", () => scheduleInit());
    document.addEventListener("shopify:section:reloaded", () => scheduleInit());
    document.addEventListener("theme:cart:update", () => scheduleInit());
    // fallback for other custom event naming patterns (if ThemeEvents is exposed)
    if (window.ThemeEvents && window.ThemeEvents.cartUpdate) {
      document.addEventListener(window.ThemeEvents.cartUpdate, () => scheduleInit());
    }
  }

  // Observe the drawer <dialog>'s open attribute to init Swiper when drawer opens
  function observeDialogOpen() {
    // adjust selector if your dialog uses a different class/name
    const dialog = document.querySelector("dialog.cart-drawer__dialog") || document.querySelector("dialog");

    if (!dialog) return;

    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === "attributes" && m.attributeName === "open") {
          if (dialog.hasAttribute("open")) {
            // schedule init shortly after open so morphing/rendering completes
            scheduleInit(50);
          }
        }
      }
    });

    mo.observe(dialog, { attributes: true });
  }

  // Run on initial load (in case drawer present on first load)
  document.addEventListener("DOMContentLoaded", () => {
    scheduleInit(50);
    attachEventListeners();
    observeDialogOpen();
  });

  // Export for manual calls if needed
  window.initAllDrawerSwipers = initAllDrawerSwipers;
})();


// // functionality to set the top height of the cart dailog.
// function updateHeaderOffset() {
//   const header = document.getElementById('header-component');
//   if (header) {
//     const headerHeight = Math.max(56, header.offsetHeight - 1);
//     document.documentElement.style.setProperty('--cart-top-height', `${headerHeight}px`);
//   }
// }

// document.addEventListener('DOMContentLoaded', updateHeaderOffset);
// window.addEventListener('resize', updateHeaderOffset);
